/**
 * Payments log — read-mostly page that lists every payment recorded
 * across all invoices, newest first. Doubles as the audit surface so
 * Finance can spot reversals + duplicate receipts quickly.
 *
 * Click a row to see full detail in a Sheet. Use "Record payment" to
 * open a new-payment Sheet with smart invoice auto-selection,
 * closest-match sorting, and inline balance calculations.
 *
 * Smart auto-selection algorithm:
 *   1. Prefer invoices that would be FULLY CLEARED (balance <= payment),
 *      sorted ascending by balance (smallest clearable first).
 *   2. If no invoice can be fully cleared, pick the one with the
 *      smallest remaining balance after payment.
 *   3. Ties are broken by oldest due date first (FIFO).
 *
 * Two-column Sheet layout:
 *   LEFT column (~55%):  Data entry inputs — dealer, amount, date, method, ref, notes
 *   RIGHT column (~45%): Live context — dealer summary, invoice list, balance breakdown
 *   On mobile the columns stack vertically.
 *
 * Standalone (on-account) payments:
 *   Invoice selection is OPTIONAL. After picking a dealer + entering an amount,
 *   the user can skip invoice selection and record an on-account payment.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Banknote,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  CreditCard,
  Filter,
  Plus,
  Receipt,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  Wallet,
  Zap,
} from "lucide-react";
import type { ColumnDef, Row } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  cn,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  KpiCard,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  PaymentMethodBadge,
  type PaymentMethod,
} from "../../../components/sales/PaymentMethodBadge";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockPayments,
  mockInvoices,
  findInvoice,
  dealerOutstandingBaseCents,
  type MockInvoice,
  type MockPayment,
} from "../../../lib/invoicingMock";

import {
  mockDealers,
  findDealerTier,
  type MockDealer,
  type MockDealerTier,
} from "../../../lib/salesMock";

type MockDealerRecord = MockDealer;

export const Route = createFileRoute("/_app/sales/payments")({
  component: PaymentsPage,
});

const NOW = Date.now();
const DAY = 24 * 60 * 60 * 1000;

const METHOD_OPTIONS: { value: PaymentMethod | "all"; label: string }[] = [
  { value: "all", label: "All methods" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "mobile_banking", label: "Mobile banking" },
  { value: "card", label: "Card" },
  { value: "credit_note", label: "Credit note" },
  { value: "other", label: "Other" },
];

const FORM_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "mobile_banking", label: "Mobile banking" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

/* -- Money helpers (BigInt throughout) -- */

function majorToCents(major: string): bigint {
  const trimmed = major.trim();
  if (trimmed === "") return 0n;
  const cleaned = trimmed.replace(/[^0-9.]/g, "");
  const [w = "", f = ""] = cleaned.split(".");
  const wholeStr = w === "" ? "0" : w;
  const frac = (f + "00").slice(0, 2);
  return BigInt(wholeStr) * 100n + BigInt(frac || "0");
}

function centsToMajor(cents: bigint): string {
  const abs = cents < 0n ? -cents : cents;
  const whole = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  const num = `${whole.toString()}.${frac}`;
  return cents < 0n ? `-${num}` : num;
}

function bigintAbs(n: bigint): bigint {
  return n < 0n ? -n : n;
}

/** Percentage (0-100) as a Number for display / width styling. Safe for UI. */
function bigintPercent(part: bigint, whole: bigint): number {
  if (whole <= 0n) return 0;
  if (part >= whole) return 100;
  if (part <= 0n) return 0;
  // Multiply by 10000 first for 2-decimal precision, then convert.
  return Number((part * 10000n) / whole) / 100;
}

/* -- Date helpers -- */

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/* -- Invoice status helpers -- */

function invoiceStatusLabel(status: MockInvoice["status"]): string {
  const labels: Record<MockInvoice["status"], string> = {
    draft: "Draft",
    issued: "Issued",
    partially_paid: "Partial",
    paid: "Paid",
    overdue: "Overdue",
    void: "Void",
  };
  return labels[status];
}

function invoiceStatusVariant(
  status: MockInvoice["status"],
): "success" | "warning" | "danger" | "neutral" | "info" {
  const map: Record<
    MockInvoice["status"],
    "success" | "warning" | "danger" | "neutral" | "info"
  > = {
    draft: "neutral",
    issued: "info",
    partially_paid: "warning",
    paid: "success",
    overdue: "danger",
    void: "neutral",
  };
  return map[status];
}

/* -- Smart auto-selection algorithm -- */

/**
 * Sorts invoices by "closest to clearing" with the given payment amount.
 *
 * Strategy:
 *   Group A -- invoices that WILL be fully cleared (balance <= payment).
 *     Sorted ascending by balance. Clearing the smallest first frees
 *     up the most invoices per BDT of payment.
 *   Group B -- invoices that will NOT be fully cleared (balance > payment).
 *     Sorted ascending by balance (smallest overshoot first).
 *   Group A always comes before Group B.
 *   Ties in each group are broken by oldest due date (FIFO).
 */
function sortByClosestMatch(
  invoices: MockInvoice[],
  paymentCents: bigint,
): MockInvoice[] {
  if (paymentCents <= 0n) {
    // No payment amount yet -- sort by oldest due date (FIFO).
    return [...invoices].sort((a, b) => a.dueDate - b.dueDate);
  }

  const clearable: MockInvoice[] = [];
  const nonClearable: MockInvoice[] = [];

  for (const inv of invoices) {
    if (inv.balanceDueCents <= paymentCents) {
      clearable.push(inv);
    } else {
      nonClearable.push(inv);
    }
  }

  // Clearable: ascending by balance (clear smallest first).
  clearable.sort((a, b) => {
    if (a.balanceDueCents < b.balanceDueCents) return -1;
    if (a.balanceDueCents > b.balanceDueCents) return 1;
    return a.dueDate - b.dueDate;
  });

  // Non-clearable: ascending by balance (smallest remaining gap first).
  nonClearable.sort((a, b) => {
    if (a.balanceDueCents < b.balanceDueCents) return -1;
    if (a.balanceDueCents > b.balanceDueCents) return 1;
    return a.dueDate - b.dueDate;
  });

  return [...clearable, ...nonClearable];
}

/** Pick the single best-match invoice for auto-selection. */
function pickBestMatch(
  invoices: MockInvoice[],
  paymentCents: bigint,
): MockInvoice | null {
  if (invoices.length === 0 || paymentCents <= 0n) return null;
  const sorted = sortByClosestMatch(invoices, paymentCents);
  return sorted[0] ?? null;
}

/* -----------------------------------------------------------------
 * Main page component
 * ----------------------------------------------------------------- */

function PaymentsPage() {
  useMockData();

  const paymentsSeed = useMemo(
    () => [...mockPayments].sort((a, b) => b.receivedAt - a.receivedAt),
    [],
  );
  const [rows, setRows] = usePersistentMockState<MockPayment[]>(
    "payments",
    paymentsSeed,
  );
  const [invoices, setInvoices] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );
  const [dealers] = usePersistentMockState<MockDealerRecord[]>(
    "dealers",
    mockDealers,
  );

  const [methodFilter, setMethodFilter] = useState<PaymentMethod | "all">(
    "all",
  );
  const [showVoided, setShowVoided] = useState(true);

  const [detailRow, setDetailRow] = useState<MockPayment | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (methodFilter !== "all" && r.paymentMethod !== methodFilter)
        return false;
      if (!showVoided && r.voidedAt !== null) return false;
      return true;
    });
  }, [rows, methodFilter, showVoided]);

  /* KPIs */
  const kpis = useMemo(() => {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const monthStart = startOfMonth.getTime();

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const dayStart = todayStart.getTime();

    let mtd = 0n;
    let today = 0n;
    let voided = 0;
    let pendingAlloc = 0;
    for (const p of rows) {
      if (p.voidedAt !== null) {
        voided += 1;
        continue;
      }
      if (p.receivedAt >= monthStart) mtd += p.amountInBaseCents;
      if (p.receivedAt >= dayStart) today += p.amountInBaseCents;
      if (!p.invoiceId) pendingAlloc += 1;
    }
    return { mtd, today, voided, pendingAlloc };
  }, [rows]);

  const dealersById = useMemo(
    () => new Map(dealers.map((d) => [d._id, d])),
    [dealers],
  );

  const columns = useMemo<ColumnDef<MockPayment>[]>(
    () => [
      {
        accessorKey: "paymentNumber",
        header: "Payment #",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <span className="font-mono text-[12px]">
            {row.original.paymentNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "invoice",
        header: "Invoice",
        cell: ({ row }: { row: Row<MockPayment> }) => {
          const inv = findInvoice(row.original.invoiceId);
          if (!row.original.invoiceId) {
            return (
              <Badge variant="neutral">On account</Badge>
            );
          }
          return (
            <span className="font-mono text-[12px] text-(--text-secondary)">
              {inv?.invoiceNumber || "—"}
            </span>
          );
        },
      },
      {
        id: "dealer",
        header: "Dealer",
        cell: ({ row }: { row: Row<MockPayment> }) => {
          // For on-account payments, use the dealerId stored directly on the payment
          if (row.original.dealerId) {
            return (
              <span className="text-(--text-primary)">
                {dealersById.get(row.original.dealerId)?.displayName ?? "—"}
              </span>
            );
          }
          const inv = findInvoice(row.original.invoiceId);
          if (!inv) return "—";
          return (
            <span className="text-(--text-primary)">
              {dealersById.get(inv.dealerId)?.displayName ?? "—"}
            </span>
          );
        },
      },
      {
        id: "method",
        header: "Method",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <PaymentMethodBadge method={row.original.paymentMethod} />
        ),
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <span
            className={cn(
              "font-mono tabular-nums",
              row.original.voidedAt !== null &&
                "line-through text-(--text-tertiary)",
            )}
          >
            {formatMoney(row.original.amountCents, row.original.currency, {
              locale: "en-BD",
              display: "code",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "amountBdt",
        header: "Amount (BDT)",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <span
            className={cn(
              "font-mono tabular-nums text-(--text-secondary)",
              row.original.voidedAt !== null && "line-through",
            )}
          >
            {formatMoney(row.original.amountInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true, hideOnMobile: true },
      },
      {
        accessorKey: "receivedAt",
        header: "Received",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.receivedAt)}
          </span>
        ),
      },
      {
        accessorKey: "externalReference",
        header: "Reference",
        cell: ({ row }: { row: Row<MockPayment> }) => (
          <span className="font-mono text-[11px] text-(--text-tertiary)">
            {row.original.externalReference ?? "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }: { row: Row<MockPayment> }) =>
          row.original.voidedAt !== null ? (
            <Badge variant="neutral">Voided</Badge>
          ) : !row.original.invoiceId ? (
            <Badge variant="warning">On account</Badge>
          ) : (
            <Badge variant="success">Posted</Badge>
          ),
      },
    ],
    [dealersById],
  );

  /* -- Record-payment Sheet state -- */
  const [selectedDealerId, setSelectedDealerId] = useState<string>("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [paymentAmountStr, setPaymentAmountStr] = useState<string>("");
  const [paymentAmountCents, setPaymentAmountCents] = useState<bigint>(0n);
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("bank_transfer");
  const [receivedAt, setReceivedAt] = useState<string>(todayIso());
  const [externalReference, setExternalReference] = useState<string>("");
  const [bankAccountName, setBankAccountName] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [justRecorded, setJustRecorded] = useState<string | null>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  /** When true, record payment without linking to any invoice. */
  const [onAccountMode, setOnAccountMode] = useState(false);

  // Unpaid invoices for selected dealer
  const unpaidInvoicesForDealer = useMemo(() => {
    if (!selectedDealerId) return [];
    return invoices.filter(
      (i) =>
        i.dealerId === selectedDealerId &&
        (i.status === "issued" || i.status === "partially_paid") &&
        i.balanceDueCents > 0n,
    );
  }, [invoices, selectedDealerId]);

  // Sorted invoice list (unpaid, by closest match, then optionally paid)
  const invoicesForDealer = useMemo(() => {
    const sortedUnpaid = sortByClosestMatch(
      unpaidInvoicesForDealer,
      paymentAmountCents,
    );
    const paid = showPaidInvoices
      ? invoices.filter(
          (i) => i.dealerId === selectedDealerId && i.status === "paid",
        )
      : [];
    return [...sortedUnpaid, ...paid];
  }, [
    unpaidInvoicesForDealer,
    invoices,
    selectedDealerId,
    paymentAmountCents,
    showPaidInvoices,
  ]);

  const unpaidCount = unpaidInvoicesForDealer.length;

  // Dealer financial summary -- computed when a dealer is selected
  const dealerFinancials = useMemo(() => {
    if (!selectedDealerId) return null;
    const dealer = dealersById.get(selectedDealerId);
    if (!dealer) return null;
    const tier = findDealerTier(dealer.tierId);

    // Total outstanding: sum of balanceDueCents for all non-void, non-draft invoices
    const totalOutstandingCents = dealerOutstandingBaseCents(
      selectedDealerId,
      invoices,
    );

    // Unpaid invoices for this dealer (issued / partially_paid / overdue with balance > 0)
    const unpaidInvs = invoices.filter(
      (i) =>
        i.dealerId === selectedDealerId &&
        i.status !== "void" &&
        i.status !== "draft" &&
        i.status !== "paid" &&
        i.balanceDueCents > 0n,
    );
    const unpaidInvoiceCount = unpaidInvs.length;

    // Oldest due date among unpaid invoices
    const oldestDueDate =
      unpaidInvs.length > 0
        ? Math.min(...unpaidInvs.map((i) => i.dueDate))
        : null;

    // Credit limit & available credit
    const creditLimitCents = dealer.creditLimitCents;
    const availableCreditCents =
      creditLimitCents > totalOutstandingCents
        ? creditLimitCents - totalOutstandingCents
        : 0n;

    // Credit utilization percentage (0-100)
    const utilizationPercent =
      creditLimitCents > 0n
        ? bigintPercent(totalOutstandingCents, creditLimitCents)
        : 0;

    // Color code: green <60%, amber 60-89%, red >=90%
    const creditStatus: "green" | "amber" | "red" =
      utilizationPercent >= 90
        ? "red"
        : utilizationPercent >= 60
          ? "amber"
          : "green";

    return {
      dealer,
      tier,
      totalOutstandingCents,
      unpaidInvoiceCount,
      oldestDueDate,
      creditLimitCents,
      availableCreditCents,
      utilizationPercent,
      creditStatus,
    };
  }, [selectedDealerId, invoices, dealersById]);

  // Auto-select: finds the best match and selects it.
  const autoSelectInvoice = useCallback(
    (amountCents: bigint, dealerId: string) => {
      if (!dealerId || amountCents <= 0n) return;
      const unpaid = invoices.filter(
        (i) =>
          i.dealerId === dealerId &&
          (i.status === "issued" || i.status === "partially_paid") &&
          i.balanceDueCents > 0n,
      );
      const best = pickBestMatch(unpaid, amountCents);
      if (best) {
        setSelectedInvoiceId(best._id);
      }
    },
    [invoices],
  );

  const selectedInvoice = useMemo(
    () => invoices.find((i) => i._id === selectedInvoiceId) ?? null,
    [invoices, selectedInvoiceId],
  );

  // Overpayment / remaining calculation
  const paymentCalc = useMemo(() => {
    if (!selectedInvoice || paymentAmountCents <= 0n) {
      return null;
    }
    const remaining = selectedInvoice.balanceDueCents - paymentAmountCents;
    const isOverpayment = remaining < 0n;
    const coveragePercent = bigintPercent(
      paymentAmountCents,
      selectedInvoice.balanceDueCents,
    );
    return {
      invoiceBalance: selectedInvoice.balanceDueCents,
      paymentAmount: paymentAmountCents,
      remaining: isOverpayment ? 0n : remaining,
      overpayment: isOverpayment ? bigintAbs(remaining) : 0n,
      isOverpayment,
      willFullyPay: remaining <= 0n,
      coveragePercent,
    };
  }, [selectedInvoice, paymentAmountCents]);

  // Whether external reference is required based on method
  const requiresReference =
    paymentMethod === "bank_transfer" ||
    paymentMethod === "cheque" ||
    paymentMethod === "mobile_banking";

  // Form validation check -- on-account mode only needs dealer + amount
  const canSubmit = onAccountMode
    ? !!selectedDealerId &&
      paymentAmountCents > 0n &&
      (!requiresReference || externalReference.trim().length > 0) &&
      !submitting
    : !!selectedInvoice &&
      paymentAmountCents > 0n &&
      (!requiresReference || externalReference.trim().length > 0) &&
      !submitting;

  // Reset all form state
  function resetFormState() {
    setSelectedDealerId("");
    setSelectedInvoiceId("");
    setPaymentAmountStr("");
    setPaymentAmountCents(0n);
    setShowPaidInvoices(false);
    setPaymentMethod("bank_transfer");
    setReceivedAt(todayIso());
    setExternalReference("");
    setBankAccountName("");
    setNotes("");
    setFormError(null);
    setJustRecorded(null);
    setOnAccountMode(false);
  }

  function openCreateSheet() {
    resetFormState();
    setCreateOpen(true);
  }

  function handleDealerChange(dealerId: string) {
    setSelectedDealerId(dealerId);
    setSelectedInvoiceId("");
    setOnAccountMode(false);
    if (paymentAmountCents > 0n) {
      // Defer auto-select so the invoice filter re-runs with the new dealer.
      setTimeout(() => autoSelectInvoice(paymentAmountCents, dealerId), 0);
    }
  }

  function handleAmountChange(value: string) {
    setPaymentAmountStr(value);
    const cents = majorToCents(value);
    setPaymentAmountCents(cents);
    if (selectedDealerId && cents > 0n && !onAccountMode) {
      autoSelectInvoice(cents, selectedDealerId);
    }
  }

  function handleToggleOnAccount(checked: boolean) {
    setOnAccountMode(checked);
    if (checked) {
      setSelectedInvoiceId("");
    } else if (selectedDealerId && paymentAmountCents > 0n) {
      // Re-trigger auto-select when switching back to invoice mode
      autoSelectInvoice(paymentAmountCents, selectedDealerId);
    }
  }

  // Open confirmation dialog
  function handlePreSubmit() {
    if (!onAccountMode && !selectedInvoice) {
      setFormError("Select an invoice to apply the payment against, or enable on-account mode.");
      return;
    }
    if (paymentAmountCents <= 0n) {
      setFormError("Amount must be positive.");
      return;
    }
    if (requiresReference && !externalReference.trim()) {
      setFormError("Reference is required for this payment method.");
      return;
    }
    setFormError(null);
    setConfirmOpen(true);
  }

  // Actually record the payment (called after confirm dialog)
  function handleCreate() {
    if (!onAccountMode && !selectedInvoice) return;

    setSubmitting(true);
    setConfirmOpen(false);

    const id = `pay_local_${Date.now()}`;
    const paymentNumber = `PAY-${new Date().getFullYear()}-${String(rows.length + 1).padStart(6, "0")}`;
    const newRow: MockPayment = {
      _id: id,
      invoiceId: onAccountMode ? null : selectedInvoice!._id,
      dealerId: selectedDealerId,
      paymentNumber,
      paymentMethod,
      currency: onAccountMode ? "BDT" : selectedInvoice!.currency,
      amountCents: paymentAmountCents,
      fxRateToBaseE8: 100_000_000n,
      amountInBaseCents: paymentAmountCents,
      externalReference: externalReference.trim() || null,
      receivedAt: new Date(receivedAt).getTime(),
      bankAccountName: bankAccountName.trim() || null,
      creditNoteId: null,
      notes: onAccountMode
        ? (notes.trim() ? `[On account] ${notes.trim()}` : "[On account] Unallocated payment")
        : (notes.trim() || null),
      voidedAt: null,
    };
    setRows((prev) => [newRow, ...prev]);

    // Update the invoice's paid amount, balance, and status -- only for invoice-linked payments.
    if (!onAccountMode && selectedInvoice) {
      setInvoices((prev) =>
        prev.map((inv) => {
          if (inv._id !== selectedInvoice._id) return inv;
          const newPaid = inv.paidAmountCents + paymentAmountCents;
          const newBalance = inv.totalAmountCents - newPaid;
          const clampedBalance = newBalance < 0n ? 0n : newBalance;
          const newPaidBase =
            inv.paidAmountInBaseCents +
            (paymentAmountCents * inv.fxRateToBaseE8) / 100_000_000n;
          const newStatus: MockInvoice["status"] =
            clampedBalance === 0n ? "paid" : "partially_paid";
          return {
            ...inv,
            paidAmountCents: newPaid,
            paidAmountInBaseCents: newPaidBase,
            balanceDueCents: clampedBalance,
            status: newStatus,
          };
        }),
      );
    }

    setSubmitting(false);
    setJustRecorded(paymentNumber);
    toast.success(
      onAccountMode
        ? `On-account payment ${paymentNumber} recorded`
        : `Payment ${paymentNumber} recorded`,
    );
  }

  function handleVoid(payment: MockPayment) {
    if (payment.voidedAt !== null) return;
    setRows((prev) =>
      prev.map((p) =>
        p._id === payment._id ? { ...p, voidedAt: Date.now() } : p,
      ),
    );

    // Reverse the invoice totals so the balance comes back -- only for invoice-linked payments.
    if (payment.invoiceId) {
      setInvoices((prev) =>
        prev.map((inv) => {
          if (inv._id !== payment.invoiceId) return inv;
          const newPaid = inv.paidAmountCents - payment.amountCents;
          const clampedPaid = newPaid < 0n ? 0n : newPaid;
          const newBalance = inv.totalAmountCents - clampedPaid;
          const clampedBalance = newBalance < 0n ? 0n : newBalance;
          const newPaidBase =
            inv.paidAmountInBaseCents - payment.amountInBaseCents;
          const clampedPaidBase = newPaidBase < 0n ? 0n : newPaidBase;
          const newStatus: MockInvoice["status"] =
            clampedPaid === 0n
              ? "issued"
              : clampedBalance === 0n
                ? "paid"
                : "partially_paid";
          return {
            ...inv,
            paidAmountCents: clampedPaid,
            paidAmountInBaseCents: clampedPaidBase,
            balanceDueCents: clampedBalance,
            status: newStatus,
          };
        }),
      );
    }

    setDetailRow(null);
    toast.success(`Payment ${payment.paymentNumber} voided`);
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockPayment>[] = [
      { key: "paymentNumber", label: "Payment #" },
      {
        key: "invoiceId",
        label: "Invoice",
        value: (r) => (r.invoiceId ? findInvoice(r.invoiceId)?.invoiceNumber ?? "" : "On account"),
      },
      {
        key: "invoiceId",
        label: "Dealer",
        value: (r) => {
          if (r.dealerId) {
            return dealersById.get(r.dealerId)?.displayName ?? "";
          }
          const inv = findInvoice(r.invoiceId);
          if (!inv) return "";
          return dealersById.get(inv.dealerId)?.displayName ?? "";
        },
      },
      { key: "paymentMethod", label: "Method" },
      { key: "amountCents", label: "Amount" },
      { key: "currency", label: "Currency" },
      { key: "amountInBaseCents", label: "Amount BDT" },
      {
        key: "receivedAt",
        label: "Received",
        format: (v) => formatDateForCsv(Number(v)),
      },
      {
        key: "externalReference",
        label: "Reference",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "voidedAt",
        label: "Voided",
        format: (v) => formatDateForCsv(v as number | null | undefined),
      },
      {
        key: "voidedAt",
        label: "Status",
        value: (r) => (r.voidedAt !== null ? "Voided" : r.invoiceId ? "Posted" : "On account"),
      },
    ];
    downloadCsv(
      exportFilename("payments", extension),
      toCsv(filtered, columns),
    );
  }

  /* -- Current step indicator for Record Payment form -- */
  const currentStep: 1 | 2 | 3 | 4 = !selectedDealerId
    ? 1
    : paymentAmountCents <= 0n
      ? 2
      : !selectedInvoiceId && !onAccountMode
        ? 3
        : 4;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payments"
        description="Every payment recorded across invoices, including reversed ones."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={openCreateSheet}>
              <Plus className="h-4 w-4" /> Record payment
            </Button>
          </>
        }
      />

      {/* KPI Strip -- scrollable on mobile */}
      <section
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
        aria-label="Payments summary"
      >
        <KpiCard
          caption="Total Collected (MTD)"
          value={formatMoneySmart(kpis.mtd, "BDT", { locale: "en-BD" })}
          hint="Month to date"
        />
        <KpiCard
          caption="Payments Today"
          value={formatMoneySmart(kpis.today, "BDT", { locale: "en-BD" })}
          hint="Since midnight"
        />
        <KpiCard
          caption="Pending Allocation"
          value={kpis.pendingAlloc}
          hint="Unmatched payments"
        />
        <KpiCard
          caption="Reversed / Voided"
          value={kpis.voided}
          hint="Lifetime reversals"
        />
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        <FilterChip
          label="Method"
          value={methodFilter}
          options={METHOD_OPTIONS.map((m) => ({
            value: m.value,
            label: m.label,
          }))}
          onChange={(v: string) => setMethodFilter(v as PaymentMethod | "all")}
        />
        <label className="inline-flex items-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-[12px]">
          <input
            type="checkbox"
            checked={showVoided}
            onChange={(e) => setShowVoided(e.target.checked)}
          />
          <span className="text-(--text-secondary)">Show voided</span>
        </label>
      </div>

      {/* Payment table */}
      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No payments match"
        emptyDescription="Try widening filters or recording a payment from an invoice."
        emptyIcon={<Banknote className="h-5 w-5" aria-hidden />}
      />

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="md">
          {detailRow ? (
            <PaymentDetail
              payment={detailRow}
              onVoid={() => handleVoid(detailRow)}
              onClose={() => setDetailRow(null)}
              dealers={dealers}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* -- Record Payment Sheet -- TWO-COLUMN LAYOUT -- */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg" className="sm:max-w-5xl">
          <SheetHeader>
            <SheetTitle>Record payment</SheetTitle>
            <SheetDescription>
              Select a dealer and enter the payment amount. The system will
              auto-match the closest invoice to clear, or record as on-account credit.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            {/* Success state */}
            {justRecorded ? (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="rounded-full bg-(--success-soft) p-4">
                  <CheckCircle2
                    className="h-10 w-10 text-(--success)"
                    aria-hidden
                  />
                </div>
                <div className="text-center">
                  <p className="text-[16px] font-semibold text-(--text-primary)">
                    Payment recorded
                  </p>
                  <p className="text-[13px] text-(--text-secondary) mt-1 font-mono">
                    {justRecorded}
                  </p>
                  {onAccountMode ? (
                    <p className="text-[12px] text-(--warning) mt-1">
                      Recorded as on-account (unallocated) payment
                    </p>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                  >
                    Close
                  </Button>
                  <Button onClick={openCreateSheet}>
                    <Plus className="h-4 w-4" /> Record another
                  </Button>
                </div>
              </div>
            ) : (
              /* -- Two-column layout: flex-col on mobile, flex-row on md+ -- */
              <div className="flex flex-col md:flex-row gap-5">
                {/* ===== LEFT COLUMN (~55%) -- Data entry inputs ===== */}
                <div className="flex flex-col gap-5 md:w-[55%] md:min-w-0">
                  {/* Step indicator -- minimal, mobile-friendly */}
                  <div className="flex items-center gap-1.5 text-[11px] font-medium">
                    <StepPill step={1} current={currentStep} label="Dealer" />
                    <ChevronRight
                      className="h-3 w-3 text-(--text-tertiary) shrink-0"
                      aria-hidden
                    />
                    <StepPill step={2} current={currentStep} label="Amount" />
                    <ChevronRight
                      className="h-3 w-3 text-(--text-tertiary) shrink-0"
                      aria-hidden
                    />
                    <StepPill step={3} current={currentStep} label="Invoice" />
                    <ChevronRight
                      className="h-3 w-3 text-(--text-tertiary) shrink-0"
                      aria-hidden
                    />
                    <StepPill step={4} current={currentStep} label="Confirm" />
                  </div>

                  {/* Step 1: Dealer selection */}
                  <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-3 sm:p-4">
                    <FieldGroup label="Dealer" htmlFor="rp-dealer" required>
                      <Select
                        value={selectedDealerId}
                        onValueChange={handleDealerChange}
                      >
                        <SelectTrigger id="rp-dealer" className="min-h-[44px]">
                          <SelectValue placeholder="Select a dealer..." />
                        </SelectTrigger>
                        <SelectContent>
                          {dealers.map((d) => (
                            <SelectItem key={d._id} value={d._id}>
                              {d.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  </div>

                  {/* Step 2: Payment amount + date -- prominent inputs */}
                  {selectedDealerId ? (
                    <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-3 sm:p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FieldGroup
                          label="Payment amount (BDT)"
                          htmlFor="rp-amount"
                          required
                        >
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-mono text-(--text-tertiary) pointer-events-none select-none">
                              &#2547;
                            </span>
                            <Input
                              ref={amountInputRef}
                              id="rp-amount"
                              inputMode="decimal"
                              className="font-mono text-[18px] sm:text-[20px] pl-7 min-h-[52px] font-semibold"
                              placeholder="0.00"
                              value={paymentAmountStr}
                              onChange={(e) => handleAmountChange(e.target.value)}
                              autoComplete="off"
                            />
                          </div>
                        </FieldGroup>
                        <FieldGroup label="Payment date" htmlFor="rp-date" required>
                          <div className="relative">
                            <Input
                              id="rp-date"
                              type="date"
                              className="min-h-[52px]"
                              value={receivedAt}
                              max={todayIso()}
                              onChange={(e) => setReceivedAt(e.target.value)}
                            />
                            {receivedAt && receivedAt < todayIso() ? (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-(--warning) bg-(--warning-soft) px-1.5 py-0.5 rounded">
                                Backdated
                              </span>
                            ) : null}
                          </div>
                        </FieldGroup>
                      </div>
                    </div>
                  ) : null}

                  {/* On-account toggle -- visible when dealer is selected and amount entered */}
                  {selectedDealerId && paymentAmountCents > 0n ? (
                    <div
                      className={cn(
                        "rounded-lg border p-3 sm:p-4 transition-all",
                        onAccountMode
                          ? "border-(--warning)/30 bg-(--warning-soft)"
                          : "border-(--border-subtle) bg-(--bg-surface)",
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[13px] font-medium text-(--text-primary)">
                            Record as on-account payment
                          </span>
                          <span className="text-[11px] text-(--text-tertiary)">
                            Skip invoice selection. Payment will be stored as unallocated dealer credit.
                          </span>
                        </div>
                        <Switch
                          checked={onAccountMode}
                          onCheckedChange={handleToggleOnAccount}
                        />
                      </div>
                    </div>
                  ) : null}

                  {/* Step 4: Payment details section -- method, reference, notes */}
                  {(selectedInvoice || onAccountMode) && selectedDealerId ? (
                    <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-3 sm:p-4">
                      <div className="flex flex-col gap-4">
                        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) font-medium">
                          Payment details
                        </span>

                        {/* Payment method */}
                        <FieldGroup
                          label="Payment method"
                          htmlFor="rp-method"
                          required
                        >
                          <Select
                            value={paymentMethod}
                            onValueChange={(v: string) =>
                              setPaymentMethod(v as PaymentMethod)
                            }
                          >
                            <SelectTrigger
                              id="rp-method"
                              className="min-h-[44px]"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FORM_METHOD_OPTIONS.map((m) => (
                                <SelectItem key={m.value} value={m.value}>
                                  {m.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FieldGroup>

                        {/* Reference */}
                        <FieldGroup
                          label={
                            requiresReference
                              ? "Reference number"
                              : "Reference number (optional)"
                          }
                          htmlFor="rp-ref"
                          required={requiresReference}
                        >
                          <Input
                            id="rp-ref"
                            className="min-h-[44px]"
                            value={externalReference}
                            onChange={(e) => setExternalReference(e.target.value)}
                            placeholder={
                              paymentMethod === "cheque"
                                ? "Cheque #"
                                : paymentMethod === "mobile_banking"
                                  ? "bKash/Nagad txn id"
                                  : "Transaction ref"
                            }
                          />
                        </FieldGroup>

                        {/* Bank account */}
                        {(paymentMethod === "bank_transfer" ||
                          paymentMethod === "cheque") && (
                          <FieldGroup label="Bank account" htmlFor="rp-bank">
                            <Input
                              id="rp-bank"
                              className="min-h-[44px]"
                              value={bankAccountName}
                              onChange={(e) => setBankAccountName(e.target.value)}
                              placeholder="Dutch-Bangla Bank -- A/C 1234..."
                            />
                          </FieldGroup>
                        )}

                        {/* Notes */}
                        <FieldGroup label="Notes (optional)" htmlFor="rp-notes">
                          <Input
                            id="rp-notes"
                            className="min-h-[44px]"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Additional notes about this payment"
                          />
                        </FieldGroup>
                      </div>
                    </div>
                  ) : selectedDealerId && unpaidCount > 0 && !onAccountMode ? (
                    <div className="text-[13px] text-(--text-tertiary) text-center py-2">
                      {paymentAmountCents > 0n
                        ? "Select an invoice from the right panel, or toggle on-account mode."
                        : "Enter a payment amount to see invoice matches."}
                    </div>
                  ) : null}

                  {/* Overpayment warning banner */}
                  {paymentCalc?.isOverpayment && !onAccountMode ? (
                    <div
                      className="flex items-start gap-2.5 rounded-lg border border-(--warning)/30 bg-(--warning-soft) p-3 text-[12px] text-(--warning)"
                      role="alert"
                    >
                      <AlertTriangle
                        className="h-4 w-4 shrink-0 mt-0.5"
                        aria-hidden
                      />
                      <div>
                        <span className="font-medium">
                          Overpayment warning:
                        </span>{" "}
                        Payment exceeds invoice balance by{" "}
                        <span className="font-mono font-semibold">
                          {formatMoney(
                            paymentCalc.overpayment,
                            selectedInvoice?.currency ?? "BDT",
                            { locale: "en-BD" },
                          )}
                        </span>
                        . The excess will remain as a credit on the invoice.
                      </div>
                    </div>
                  ) : null}

                  {/* Form error */}
                  {formError ? (
                    <div
                      className="flex items-center gap-2 rounded-lg border border-(--danger)/30 bg-(--danger-soft) px-3 py-2 text-[12px] text-(--danger)"
                      role="alert"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {formError}
                    </div>
                  ) : null}
                </div>

                {/* ===== RIGHT COLUMN (~45%) -- Live context panel (sticky) ===== */}
                <div className="md:w-[45%] md:min-w-0">
                  <div className="md:sticky md:top-0 flex flex-col gap-4">
                    {/* Dealer Financial Summary */}
                    {dealerFinancials ? (
                      <DealerFinancialSummary
                        financials={dealerFinancials}
                        paymentAmountCents={paymentAmountCents}
                      />
                    ) : (
                      <div className="rounded-lg border border-dashed border-(--border-subtle) bg-(--bg-surface) p-6 text-center">
                        <Wallet className="h-5 w-5 mx-auto mb-2 text-(--text-tertiary)" aria-hidden />
                        <p className="text-[13px] text-(--text-tertiary)">
                          Select a dealer to see their financial summary.
                        </p>
                      </div>
                    )}

                    {/* Real-time balance update -- live summary as user types amount */}
                    {paymentAmountCents > 0n && dealerFinancials ? (
                      <div className="rounded-lg bg-(--bg-canvas) border border-(--border-subtle) p-3">
                        <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) font-medium block mb-2">
                          Balance breakdown
                        </span>
                        <div className="font-mono text-[12px] tabular-nums space-y-1.5">
                          <div className="flex justify-between">
                            <span className="text-(--text-secondary)">
                              Total outstanding before
                            </span>
                            <span className="text-(--text-primary)">
                              {formatMoney(
                                dealerFinancials.totalOutstandingCents,
                                "BDT",
                                { locale: "en-BD" },
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-(--success)">
                            <span>This payment</span>
                            <span>
                              &minus;{" "}
                              {formatMoney(paymentAmountCents, "BDT", {
                                locale: "en-BD",
                                display: "none",
                              })}
                            </span>
                          </div>
                          <div className="border-t border-(--border-subtle) my-1" />
                          <div className="flex justify-between">
                            <span
                              className={cn(
                                "font-medium",
                                dealerFinancials.totalOutstandingCents <=
                                  paymentAmountCents
                                  ? "text-(--success)"
                                  : "text-(--text-primary)",
                              )}
                            >
                              Remaining after payment
                            </span>
                            <span
                              className={cn(
                                "font-semibold",
                                dealerFinancials.totalOutstandingCents <=
                                  paymentAmountCents
                                  ? "text-(--success)"
                                  : "text-(--text-primary)",
                              )}
                            >
                              {formatMoney(
                                dealerFinancials.totalOutstandingCents >
                                  paymentAmountCents
                                  ? dealerFinancials.totalOutstandingCents -
                                      paymentAmountCents
                                  : 0n,
                                "BDT",
                                { locale: "en-BD" },
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Coverage / selected invoice detail -- only when an invoice is selected */}
                    {selectedInvoice && paymentCalc && !onAccountMode ? (
                      <div className="rounded-lg bg-(--bg-canvas) border border-(--border-subtle) p-3">
                        {/* Coverage percentage header */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[12px] text-(--text-secondary)">
                            Coverage of{" "}
                            <span className="font-mono font-medium text-(--text-primary)">
                              {selectedInvoice.invoiceNumber}
                            </span>
                          </span>
                          <span
                            className={cn(
                              "text-[14px] font-semibold font-mono tabular-nums",
                              paymentCalc.willFullyPay
                                ? "text-(--success)"
                                : "text-(--text-primary)",
                            )}
                          >
                            {Math.min(paymentCalc.coveragePercent, 100).toFixed(0)}%
                          </span>
                        </div>

                        {/* Coverage bar */}
                        <div className="w-full h-2 rounded-full bg-(--border-subtle) overflow-hidden mb-3">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all duration-300 ease-out",
                              paymentCalc.isOverpayment
                                ? "bg-(--warning)"
                                : paymentCalc.willFullyPay
                                  ? "bg-(--success)"
                                  : "bg-(--accent)",
                            )}
                            style={{
                              width: `${Math.min(paymentCalc.coveragePercent, 100)}%`,
                            }}
                          />
                        </div>

                        {/* Breakdown */}
                        <div className="font-mono text-[12px] tabular-nums space-y-1">
                          <div className="flex justify-between">
                            <span className="text-(--text-secondary)">
                              Invoice balance
                            </span>
                            <span className="text-(--text-primary)">
                              {formatMoney(
                                paymentCalc.invoiceBalance,
                                selectedInvoice.currency,
                                { locale: "en-BD" },
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-(--text-secondary)">
                              This payment
                            </span>
                            <span className="text-(--text-primary)">
                              -{" "}
                              {formatMoney(
                                paymentCalc.paymentAmount,
                                selectedInvoice.currency,
                                { locale: "en-BD", display: "none" },
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-(--border-subtle)">
                            <span
                              className={cn(
                                "font-medium",
                                paymentCalc.isOverpayment
                                  ? "text-(--warning)"
                                  : paymentCalc.willFullyPay
                                    ? "text-(--success)"
                                    : "text-(--text-primary)",
                              )}
                            >
                              {paymentCalc.isOverpayment
                                ? "Overpayment"
                                : paymentCalc.willFullyPay
                                  ? "Fully cleared"
                                  : "Remaining"}
                            </span>
                            <span
                              className={cn(
                                "font-semibold",
                                paymentCalc.isOverpayment
                                  ? "text-(--warning)"
                                  : paymentCalc.willFullyPay
                                    ? "text-(--success)"
                                    : "text-(--text-primary)",
                              )}
                            >
                              {paymentCalc.isOverpayment
                                ? formatMoney(
                                    paymentCalc.overpayment,
                                    selectedInvoice.currency,
                                    { locale: "en-BD" },
                                  )
                                : formatMoney(
                                    paymentCalc.remaining,
                                    selectedInvoice.currency,
                                    { locale: "en-BD" },
                                  )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : onAccountMode && paymentAmountCents > 0n ? (
                      <div className="rounded-lg border border-(--warning)/30 bg-(--warning-soft) p-3">
                        <div className="flex items-center gap-2 text-[12px] text-(--warning)">
                          <Wallet className="h-4 w-4 shrink-0" aria-hidden />
                          <div>
                            <span className="font-medium">On-account payment</span>
                            <p className="text-[11px] mt-0.5 text-(--text-secondary)">
                              This payment will not be linked to any invoice. It will appear as
                              an unallocated credit on the dealer's account.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Invoice selection list -- shown on the right panel */}
                    {selectedDealerId && !onAccountMode ? (
                      <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-3 sm:p-4">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) font-medium">
                                Invoices
                              </span>
                              {unpaidCount > 0 ? (
                                <Badge variant="info">{unpaidCount} unpaid</Badge>
                              ) : null}
                            </div>
                            <label className="inline-flex items-center gap-2 cursor-pointer min-h-[44px] sm:min-h-0">
                              <span className="text-[12px] text-(--text-secondary)">
                                Show paid
                              </span>
                              <Switch
                                checked={showPaidInvoices}
                                onCheckedChange={setShowPaidInvoices}
                              />
                            </label>
                          </div>

                          {/* Auto-select hint */}
                          {paymentAmountCents > 0n &&
                          selectedInvoiceId &&
                          unpaidCount > 0 ? (
                            <div className="flex items-center gap-1.5 rounded-md bg-(--accent-soft)/10 border border-(--accent)/20 px-2.5 py-1.5 text-[11px] text-(--accent)">
                              <Zap className="h-3 w-3 shrink-0" aria-hidden />
                              <span>
                                Auto-selected best match for{" "}
                                <span className="font-mono font-semibold">
                                  {formatMoney(paymentAmountCents, "BDT", {
                                    locale: "en-BD",
                                  })}
                                </span>
                              </span>
                            </div>
                          ) : null}

                          {invoicesForDealer.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-(--border-subtle) p-6 text-center">
                              <Receipt
                                className="h-5 w-5 mx-auto mb-2 text-(--text-tertiary)"
                                aria-hidden
                              />
                              <p className="text-[13px] text-(--text-tertiary)">
                                {unpaidCount === 0 && !showPaidInvoices
                                  ? "No unpaid invoices for this dealer. Toggle 'Show paid' to see all."
                                  : "No invoices found for this dealer."}
                              </p>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto -mx-1 px-1">
                              {invoicesForDealer.map((inv, idx) => (
                                <InvoiceCard
                                  key={inv._id}
                                  invoice={inv}
                                  isSelected={inv._id === selectedInvoiceId}
                                  paymentCents={paymentAmountCents}
                                  isBestMatch={
                                    idx === 0 &&
                                    paymentAmountCents > 0n &&
                                    inv.status !== "paid"
                                  }
                                  onSelect={() => setSelectedInvoiceId(inv._id)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : selectedDealerId && onAccountMode ? null : !selectedDealerId ? (
                      <div className="rounded-lg border border-dashed border-(--border-subtle) p-6 text-center">
                        <p className="text-[13px] text-(--text-tertiary)">
                          Choose a dealer to see their invoices.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </SheetBody>

          {/* Footer -- sticky on mobile */}
          {!justRecorded ? (
            <SheetFooter className="border-t border-(--border-subtle) bg-(--bg-surface)">
              <Button
                variant="ghost"
                onClick={() => setCreateOpen(false)}
                type="button"
                className="min-h-[44px]"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePreSubmit}
                loading={submitting}
                disabled={!canSubmit}
                type="button"
                className="min-h-[44px]"
              >
                {onAccountMode ? (
                  "Record on-account payment"
                ) : paymentCalc?.willFullyPay ? (
                  <>
                    <Check className="h-4 w-4" /> Record & clear invoice
                  </>
                ) : (
                  "Record payment"
                )}
              </Button>
            </SheetFooter>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {onAccountMode ? "Confirm on-account payment" : "Confirm payment"}
            </DialogTitle>
            <DialogDescription>
              {onAccountMode ? (
                <>
                  Record{" "}
                  <span className="font-mono font-semibold">
                    {formatMoney(paymentAmountCents, "BDT", {
                      locale: "en-BD",
                    })}
                  </span>{" "}
                  as an unallocated on-account payment for{" "}
                  <span className="font-semibold">
                    {dealersById.get(selectedDealerId)?.displayName ?? "—"}
                  </span>
                  . This payment will not be linked to any invoice and will appear as
                  dealer credit until manually allocated.
                </>
              ) : selectedInvoice ? (
                <>
                  Record{" "}
                  <span className="font-mono font-semibold">
                    {formatMoney(paymentAmountCents, "BDT", {
                      locale: "en-BD",
                    })}
                  </span>{" "}
                  against{" "}
                  <span className="font-mono font-semibold">
                    {selectedInvoice.invoiceNumber}
                  </span>
                  {paymentCalc?.willFullyPay
                    ? ". This will fully clear the invoice."
                    : paymentCalc?.isOverpayment
                      ? `. This exceeds the balance by ${formatMoney(paymentCalc.overpayment, "BDT", { locale: "en-BD" })}.`
                      : `. Remaining balance: ${formatMoney(paymentCalc?.remaining ?? 0n, "BDT", { locale: "en-BD" })}.`}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-(--bg-canvas) border border-(--border-subtle) p-3 font-mono text-[12px] tabular-nums space-y-1">
            <div className="flex justify-between">
              <span className="text-(--text-secondary)">Dealer</span>
              <span className="text-(--text-primary) font-sans">
                {dealersById.get(selectedDealerId)?.displayName ?? "—"}
              </span>
            </div>
            {onAccountMode ? (
              <div className="flex justify-between">
                <span className="text-(--text-secondary)">Type</span>
                <span className="text-(--warning) font-sans font-medium">
                  On account (unallocated)
                </span>
              </div>
            ) : selectedInvoice ? (
              <div className="flex justify-between">
                <span className="text-(--text-secondary)">Invoice</span>
                <span className="text-(--text-primary)">
                  {selectedInvoice.invoiceNumber}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between">
              <span className="text-(--text-secondary)">Amount</span>
              <span className="text-(--text-primary) font-semibold">
                {formatMoney(paymentAmountCents, "BDT", {
                  locale: "en-BD",
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-(--text-secondary)">Method</span>
              <span className="text-(--text-primary) font-sans capitalize">
                {paymentMethod.replace("_", " ")}
              </span>
            </div>
            {externalReference.trim() ? (
              <div className="flex justify-between">
                <span className="text-(--text-secondary)">Reference</span>
                <span className="text-(--text-primary)">
                  {externalReference.trim()}
                </span>
              </div>
            ) : null}
            {dealerFinancials ? (
              <>
                <div className="border-t border-(--border-subtle) my-1.5" />
                <div className="flex justify-between">
                  <span className="text-(--text-secondary)">
                    Dealer total outstanding
                  </span>
                  <span className="text-(--text-primary)">
                    {formatMoney(
                      dealerFinancials.totalOutstandingCents,
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-(--text-secondary)">
                    After this payment
                  </span>
                  <span
                    className={cn(
                      "font-semibold",
                      dealerFinancials.totalOutstandingCents <=
                        paymentAmountCents
                        ? "text-(--success)"
                        : "text-(--text-primary)",
                    )}
                  >
                    {formatMoney(
                      dealerFinancials.totalOutstandingCents >
                        paymentAmountCents
                        ? dealerFinancials.totalOutstandingCents -
                            paymentAmountCents
                        : 0n,
                      "BDT",
                      { locale: "en-BD" },
                    )}
                  </span>
                </div>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              loading={submitting}
              className="min-h-[44px]"
            >
              Confirm & record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Dealer Financial Summary -- full financial context card
 *
 * Shown in the RIGHT column as soon as a dealer is selected.
 * Displays total outstanding, unpaid invoice count, oldest due date,
 * credit limit, available credit, and a utilization status indicator.
 * ----------------------------------------------------------------- */

type DealerFinancialsData = {
  dealer: MockDealer;
  tier: MockDealerTier | null;
  totalOutstandingCents: bigint;
  unpaidInvoiceCount: number;
  oldestDueDate: number | null;
  creditLimitCents: bigint;
  availableCreditCents: bigint;
  utilizationPercent: number;
  creditStatus: "green" | "amber" | "red";
};

function DealerFinancialSummary({
  financials,
  paymentAmountCents,
}: {
  financials: DealerFinancialsData;
  paymentAmountCents: bigint;
}) {
  const {
    dealer,
    tier,
    totalOutstandingCents,
    unpaidInvoiceCount,
    oldestDueDate,
    creditLimitCents,
    availableCreditCents,
    utilizationPercent,
    creditStatus,
  } = financials;

  const statusConfig = {
    green: {
      label: "Good standing",
      bg: "bg-(--success-soft)",
      text: "text-(--success)",
      border: "border-(--success)/30",
      barColor: "bg-(--success)",
    },
    amber: {
      label: "Approaching limit",
      bg: "bg-(--warning-soft)",
      text: "text-(--warning)",
      border: "border-(--warning)/30",
      barColor: "bg-(--warning)",
    },
    red: {
      label: "Near/over limit",
      bg: "bg-(--danger-soft)",
      text: "text-(--danger)",
      border: "border-(--danger)/30",
      barColor: "bg-(--danger)",
    },
  }[creditStatus];

  return (
    <div
      className={cn(
        "rounded-lg border p-3 sm:p-4 transition-all",
        statusConfig.border,
        statusConfig.bg,
      )}
      role="region"
      aria-label="Dealer financial summary"
    >
      {/* Header: dealer name + tier badge */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Wallet
            className="h-4 w-4 shrink-0 text-(--text-secondary)"
            aria-hidden
          />
          <span className="text-[14px] font-semibold text-(--text-primary) truncate">
            {dealer.displayName}
          </span>
        </div>
        {tier ? (
          <Badge
            variant={
              tier.code === "platinum"
                ? "info"
                : tier.code === "gold"
                  ? "warning"
                  : tier.code === "silver"
                    ? "neutral"
                    : "neutral"
            }
          >
            {tier.displayName}
          </Badge>
        ) : null}
      </div>

      {/* Financial metrics grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[12px]">
        {/* Total outstanding */}
        <div className="flex flex-col gap-0.5">
          <span className="text-(--text-tertiary) text-[11px] uppercase tracking-wide">
            Total Outstanding
          </span>
          <span className="font-mono font-semibold tabular-nums text-(--text-primary) text-[14px]">
            {formatMoney(totalOutstandingCents, "BDT", { locale: "en-BD" })}
          </span>
        </div>

        {/* Unpaid invoice count */}
        <div className="flex flex-col gap-0.5">
          <span className="text-(--text-tertiary) text-[11px] uppercase tracking-wide">
            Unpaid Invoices
          </span>
          <span className="font-mono font-semibold tabular-nums text-(--text-primary) text-[14px]">
            {unpaidInvoiceCount}
          </span>
        </div>

        {/* Oldest due date */}
        <div className="flex flex-col gap-0.5">
          <span className="text-(--text-tertiary) text-[11px] uppercase tracking-wide">
            Oldest Due
          </span>
          <span className="text-(--text-primary) text-[13px]">
            {oldestDueDate ? formatDate(oldestDueDate) : "None"}
          </span>
        </div>

        {/* Credit limit */}
        <div className="flex flex-col gap-0.5">
          <span className="text-(--text-tertiary) text-[11px] uppercase tracking-wide">
            Credit Limit
          </span>
          <span className="font-mono tabular-nums text-(--text-primary) text-[13px]">
            {formatMoney(creditLimitCents, "BDT", { locale: "en-BD" })}
          </span>
        </div>

        {/* Available credit -- full width */}
        <div className="col-span-2 flex flex-col gap-0.5">
          <span className="text-(--text-tertiary) text-[11px] uppercase tracking-wide">
            Available Credit
          </span>
          <span
            className={cn(
              "font-mono font-semibold tabular-nums text-[14px]",
              creditStatus === "red"
                ? "text-(--danger)"
                : creditStatus === "amber"
                  ? "text-(--warning)"
                  : "text-(--success)",
            )}
          >
            {formatMoney(availableCreditCents, "BDT", { locale: "en-BD" })}
          </span>
        </div>
      </div>

      {/* Credit utilization bar */}
      <div className="mt-3 pt-3 border-t border-(--border-subtle)/50">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="flex items-center gap-1.5">
            <CreditCard className="h-3 w-3 shrink-0" aria-hidden />
            <span className="text-(--text-secondary)">Credit utilization</span>
          </span>
          <span className={cn("font-mono tabular-nums font-medium", statusConfig.text)}>
            {utilizationPercent.toFixed(0)}%
          </span>
        </div>
        <div className="w-full h-2 rounded-full bg-(--bg-canvas) overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300 ease-out",
              statusConfig.barColor,
            )}
            style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span
            className={cn(
              "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full",
              statusConfig.bg,
              statusConfig.text,
            )}
          >
            <ShieldCheck className="h-2.5 w-2.5" aria-hidden />
            {statusConfig.label}
          </span>
          {paymentAmountCents > 0n ? (
            <span className="text-[10px] text-(--text-tertiary) font-mono tabular-nums">
              After payment:{" "}
              {bigintPercent(
                totalOutstandingCents > paymentAmountCents
                  ? totalOutstandingCents - paymentAmountCents
                  : 0n,
                creditLimitCents,
              ).toFixed(0)}
              %
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Invoice Card -- rich, touch-friendly invoice selection item
 * ----------------------------------------------------------------- */

function InvoiceCard({
  invoice,
  isSelected,
  paymentCents,
  isBestMatch,
  onSelect,
}: {
  invoice: MockInvoice;
  isSelected: boolean;
  paymentCents: bigint;
  isBestMatch: boolean;
  onSelect: () => void;
}) {
  const isPaid = invoice.status === "paid";
  const hasPaymentAmount = paymentCents > 0n;

  // Coverage calculation for the match indicator
  const coveragePercent =
    hasPaymentAmount && !isPaid
      ? bigintPercent(paymentCents, invoice.balanceDueCents)
      : 0;

  const wouldClear =
    hasPaymentAmount && !isPaid && paymentCents >= invoice.balanceDueCents;

  const remainingAfterPayment =
    !isPaid && hasPaymentAmount
      ? invoice.balanceDueCents - paymentCents
      : null;

  return (
    <button
      type="button"
      disabled={isPaid}
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-lg border p-3 sm:p-4 transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--border-focus)",
        "min-h-[64px] touch-manipulation",
        isPaid
          ? "border-(--border-subtle) bg-(--bg-canvas) opacity-50 cursor-not-allowed"
          : isSelected
            ? "border-(--accent) ring-2 ring-(--accent)/30 bg-(--accent-soft)/5"
            : "border-(--border-subtle) bg-(--bg-surface) hover:border-(--border-default) hover:bg-(--bg-hover) cursor-pointer active:scale-[0.99]",
      )}
    >
      {/* Header row: invoice number, badges */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] font-medium text-(--text-primary)">
              {invoice.invoiceNumber || "(draft)"}
            </span>
            <Badge variant={invoiceStatusVariant(invoice.status)}>
              {invoiceStatusLabel(invoice.status)}
            </Badge>
            {isBestMatch && !isPaid ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-(--success-soft) px-1.5 py-0.5 text-[10px] font-semibold text-(--success) uppercase tracking-wide">
                <Sparkles className="h-2.5 w-2.5" aria-hidden />
                Best match
              </span>
            ) : null}
            {isSelected && !isPaid ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-(--accent)">
                <Check className="h-3 w-3" aria-hidden />
                Selected
              </span>
            ) : null}
          </div>
          <span className="text-[12px] text-(--text-secondary)">
            Due {formatDate(invoice.dueDate)}
            {" · "}
            Total{" "}
            {formatMoney(invoice.totalAmountCents, invoice.currency, {
              locale: "en-BD",
            })}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {!isPaid ? (
            <span className="font-mono text-[14px] font-semibold tabular-nums text-(--text-primary)">
              {formatMoney(invoice.balanceDueCents, invoice.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-[12px] text-(--text-tertiary)">
              Fully paid
            </span>
          )}
          <span className="text-[11px] text-(--text-tertiary)">
            {isPaid ? "" : "balance due"}
          </span>
        </div>
      </div>

      {/* Match indicator -- progress bar showing coverage */}
      {hasPaymentAmount && !isPaid ? (
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-(--text-tertiary)">
              Payment coverage
            </span>
            <span
              className={cn(
                "font-mono tabular-nums font-medium",
                wouldClear ? "text-(--success)" : "text-(--text-secondary)",
              )}
            >
              {Math.min(coveragePercent, 100).toFixed(0)}%
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-(--border-subtle) overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300 ease-out",
                wouldClear
                  ? coveragePercent > 100
                    ? "bg-(--warning)"
                    : "bg-(--success)"
                  : "bg-(--accent)",
              )}
              style={{
                width: `${Math.min(coveragePercent, 100)}%`,
              }}
            />
          </div>
        </div>
      ) : null}

      {/* After-payment calculation (only on selected) */}
      {remainingAfterPayment !== null && isSelected ? (
        <div
          className={cn(
            "mt-2 pt-2 border-t border-(--border-subtle) text-[12px] font-mono tabular-nums",
            remainingAfterPayment < 0n
              ? "text-(--warning)"
              : remainingAfterPayment === 0n
                ? "text-(--success)"
                : "text-(--text-secondary)",
          )}
        >
          {remainingAfterPayment < 0n ? (
            <span className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
              Overpayment of{" "}
              {formatMoney(bigintAbs(remainingAfterPayment), invoice.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : remainingAfterPayment === 0n ? (
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden />
              Invoice will be fully cleared
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <TrendingDown className="h-3 w-3 shrink-0" aria-hidden />
              Remaining after payment:{" "}
              {formatMoney(remainingAfterPayment, invoice.currency, {
                locale: "en-BD",
              })}
            </span>
          )}
        </div>
      ) : null}
    </button>
  );
}

/* -----------------------------------------------------------------
 * Step pill -- visual indicator for the form flow
 * ----------------------------------------------------------------- */

function StepPill({
  step,
  current,
  label,
}: {
  step: number;
  current: number;
  label: string;
}) {
  const isComplete = current > step;
  const isCurrent = current === step;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] transition-colors",
        isComplete
          ? "bg-(--success-soft) text-(--success)"
          : isCurrent
            ? "bg-(--accent-soft)/20 text-(--accent) font-semibold"
            : "bg-(--bg-canvas) text-(--text-tertiary)",
      )}
    >
      {isComplete ? (
        <Check className="h-2.5 w-2.5" aria-hidden />
      ) : (
        <CircleDot className="h-2.5 w-2.5" aria-hidden />
      )}
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{step}</span>
    </span>
  );
}

/* -----------------------------------------------------------------
 * Payment detail Sheet body
 * ----------------------------------------------------------------- */

function PaymentDetail({
  payment,
  onVoid,
  onClose,
  dealers,
}: {
  payment: MockPayment;
  onVoid: () => void;
  onClose: () => void;
  dealers: MockDealerRecord[];
}) {
  const invoice = findInvoice(payment.invoiceId);
  const dealer = useMemo(() => {
    // Use dealerId from payment directly if available, otherwise resolve from invoice
    if (payment.dealerId) {
      return dealers.find((d) => d._id === payment.dealerId);
    }
    return invoice ? dealers.find((d) => d._id === invoice.dealerId) : undefined;
  }, [payment, invoice, dealers]);
  const isVoided = payment.voidedAt !== null;
  const isOnAccount = !payment.invoiceId;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{payment.paymentNumber}</span>
          {isVoided ? (
            <Badge variant="neutral">Voided</Badge>
          ) : isOnAccount ? (
            <Badge variant="warning">On account</Badge>
          ) : (
            <Badge variant="success">Posted</Badge>
          )}
        </SheetTitle>
        <SheetDescription>
          {isOnAccount
            ? "On-account (unallocated)"
            : (invoice?.invoiceNumber ?? "—")}
          {dealer ? ` · ${dealer.displayName}` : null}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Amount"
              value={formatMoney(payment.amountCents, payment.currency, {
                display: "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="In BDT"
              value={formatMoney(payment.amountInBaseCents, "BDT", {
                locale: "en-BD",
              })}
            />
            <DetailField
              label="Method"
              value={<PaymentMethodBadge method={payment.paymentMethod} />}
            />
            <DetailField
              label="Received"
              value={formatDate(payment.receivedAt)}
            />
            <DetailField
              label="Reference"
              value={payment.externalReference ?? "—"}
            />
            <DetailField
              label="Bank account"
              value={payment.bankAccountName ?? "—"}
            />
            {isOnAccount ? (
              <DetailField
                label="Allocation"
                value={
                  <span className="text-(--warning) font-sans text-[12px]">
                    Unallocated (on account)
                  </span>
                }
              />
            ) : null}
            {isVoided ? (
              <DetailField
                label="Voided at"
                value={formatDateTime(payment.voidedAt!)}
              />
            ) : null}
          </section>

          {payment.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {payment.notes}
              </p>
            </section>
          ) : null}
        </div>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose} className="min-h-[44px]">
          Close
        </Button>
        {!isVoided ? (
          <Button
            variant="destructive"
            onClick={onVoid}
            className="min-h-[44px]"
          >
            Void payment
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* -----------------------------------------------------------------
 * Shared sub-components
 * ----------------------------------------------------------------- */

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}

function FieldGroup({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="text-[var(--danger)] ml-1" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      {children}
    </div>
  );
}

function FilterChip<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-[12px]">
      <span className="text-(--text-tertiary)">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-transparent text-(--text-primary) outline-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
